import {
	format,
	formatDistance,
	isToday,
	millisecondsInHour,
	millisecondsInMinute,
	millisecondsInSecond,
	millisecondsToHours,
	secondsToMilliseconds,
} from "@suse-o11y/date-time";
import { dropWhile } from "@suse-o11y/drop-while";
import { floor } from "@suse-o11y/floor";
import type { Record } from "@suse-o11y/record";

import { convertTimeFrom } from "./convert-time-from";
import type { PrecisionCount, UnitFormatter, UnitFormatterWithOptions } from "./types";

export const fromNanoSeconds: UnitFormatterWithOptions = (nanoseconds, { precision } = {}) =>
	convertTimeFrom("nanosecond", nanoseconds, precision, { maximumUnit: "day" });

export const fromMicroSeconds: UnitFormatterWithOptions = (microseconds, { precision } = {}) =>
	convertTimeFrom("microsecond", microseconds, precision, { maximumUnit: "second" });

export const fromMilliSeconds: UnitFormatterWithOptions = (milliseconds, { precision } = {}) =>
	convertTimeFrom("millisecond", milliseconds, precision);

export const fromSeconds: UnitFormatterWithOptions = (seconds, { precision } = {}) =>
	convertTimeFrom("second", seconds, precision, { minimalUnit: "nanosecond" });

export const fromMinutes: UnitFormatterWithOptions = (minutes, { precision } = {}) =>
	convertTimeFrom("minute", minutes, precision);

export const fromHours: UnitFormatterWithOptions = (hours, { precision } = {}) =>
	convertTimeFrom("hour", hours, precision);

export const fromDays: UnitFormatterWithOptions = (days, { precision } = {}) => convertTimeFrom("day", days, precision);

const DURATION_INTERVALS = ["years", "months", "weeks", "days", "hours", "minutes", "seconds", "milliseconds"] as const;

export type DurationInterval = (typeof DURATION_INTERVALS)[number];

const DURATION_INTERVALS_IN_MILLISECONDS: Record<DurationInterval, number> = {
	years: millisecondsInHour * 24 * 365,
	months: millisecondsInHour * 24 * 30,
	weeks: millisecondsInHour * 24 * 7,
	days: millisecondsInHour * 24,
	hours: millisecondsInHour,
	minutes: millisecondsInMinute,
	seconds: millisecondsInSecond,
	milliseconds: 1,
};

type Duration = Record<DurationInterval, number>;

const ZERO_DURATION: Duration = {
	years: 0,
	months: 0,
	weeks: 0,
	days: 0,
	hours: 0,
	minutes: 0,
	seconds: 0,
	milliseconds: 0,
};

const millisecondsToDuration = (
	ms: number,
	intervals: ReadonlyArray<DurationInterval> = DURATION_INTERVALS,
): Duration => {
	const intervalsSet = new Set(intervals);

	let remainingMs = ms;

	return DURATION_INTERVALS.filter((interval) => intervalsSet.has(interval)).reduce((acc, interval) => {
		const intervalMs = DURATION_INTERVALS_IN_MILLISECONDS[interval];
		const value = floor(remainingMs / intervalMs, 0);

		remainingMs %= intervalMs;

		return { ...acc, [interval]: value };
	}, ZERO_DURATION);
};

export const toDuration = (count: number, timeScale: DurationInterval, precision: PrecisionCount): string => {
	if (count < 0) {
		return `${toDuration(-count, timeScale, precision)} ago`;
	}

	if (count === 0) {
		return `0 ${timeScale}`;
	}

	const duration = millisecondsToDuration(count * DURATION_INTERVALS_IN_MILLISECONDS[timeScale]);

	return dropWhile(DURATION_INTERVALS, (interval) => duration[interval] < 1) // drop all leading 0 durations
		.slice(0, 1 + (precision ?? Number.POSITIVE_INFINITY)) // take the remaining intervals according the precision
		.map((interval) => {
			const value = duration[interval];
			const plural = value === 1 ? interval.replace(/s$/u, "") : interval;

			return `${value} ${plural}`;
		})
		.join(", ");
};

const toMilliSecondsClock = (ms: number): string => format(ms, "SSS'ms'");

const toSecondsClock = (ms: number, precision: PrecisionCount): string => {
	const seconds = format(ms, "ss's'");

	if (precision === 0) {
		return seconds;
	}

	return `${seconds}:${toMilliSecondsClock(ms)}`;
};

const toMinutesClock = (ms: number, precision: PrecisionCount): string => {
	const minutes = format(ms, "mm'm'");

	if (precision === 0) {
		return minutes;
	}

	return `${minutes}:${toSecondsClock(ms, (precision ?? 2) - 1)}`;
};

export const toClock = (ms: number, precision?: PrecisionCount): string => {
	if (ms < millisecondsInSecond) {
		return toMilliSecondsClock(ms);
	}

	if (ms < millisecondsInMinute) {
		return toSecondsClock(ms, precision);
	}

	if (ms < millisecondsInHour) {
		return toMinutesClock(ms, precision);
	}

	// depending on timezone `new Date(0)` will produce different data.hour value
	// moreover hours are values [0, 23] but here we should format wider [1, +Infinity) range of values
	const hours = millisecondsToHours(ms).toString().padStart(2, "0");

	if (precision === 0) {
		return `${hours}h`;
	}

	return `${hours}h:${toMinutesClock(ms, (precision ?? 3) - 1)}`;
};

export const toDurationInMilliseconds: UnitFormatterWithOptions = (ms, { precision } = {}) =>
	toDuration(ms, "milliseconds", precision);

export const toDurationInSeconds: UnitFormatterWithOptions = (seconds, { precision } = {}) =>
	toDuration(seconds, "seconds", precision);

const toDurationInDHMS = (seconds: number, { includeDays }: { includeDays: boolean }): string => {
	if (seconds < 0) {
		return `${toDurationInDHMS(-seconds, { includeDays })} ago`;
	}

	const duration = millisecondsToDuration(
		secondsToMilliseconds(seconds),
		includeDays ? ["days", "hours", "minutes", "seconds"] : ["hours", "minutes", "seconds"],
	);

	const hms = [duration.hours, duration.minutes, duration.seconds].map((x) => x.toString().padStart(2, "0")).join(":");

	if (!includeDays || duration.days === 0) {
		return hms;
	}

	return `${duration.days} d ${hms}`;
};

export const toDurationInHoursMinutesSeconds: UnitFormatter = (seconds) =>
	toDurationInDHMS(seconds, { includeDays: false });

export const toDurationInDaysHoursMinutesSeconds: UnitFormatter = (seconds) =>
	toDurationInDHMS(seconds, { includeDays: true });

export const toTimeTicks: UnitFormatterWithOptions = (ticks, options) => fromSeconds(ticks / 100, options);

export const toClockMilliseconds: UnitFormatterWithOptions = (ms, { precision } = {}) => toClock(ms, precision);

export const toClockSeconds: UnitFormatterWithOptions = (seconds, { precision } = {}) =>
	toClock(secondsToMilliseconds(seconds), precision);

const toDateTimeValueFormatter =
	(pattern: string, todayPattern = pattern): UnitFormatter =>
	(ms) =>
		format(ms, isToday(ms) ? todayPattern : pattern);

export const dateTimeAsIso = toDateTimeValueFormatter("yyy-MM-dd HH:mm:ss");
export const dateTimeAsIsoNoDateIfToday = toDateTimeValueFormatter("yyy-MM-dd HH:mm:ss", "HH:mm:ss");
export const dateTimeAsUS = toDateTimeValueFormatter("MM/dd/yyy h:mm:ss a");
export const dateTimeAsUSNoDateIfToday = toDateTimeValueFormatter("MM/dd/yyy h:mm:ss a", "h:mm:ss a");

const DEFAULT_SYSTEM_DATE_FORMAT = "yyy-MM-dd HH:mm:ss";

const hasIntlDateTimeFormatSupport = (): boolean =>
	"DateTimeFormat" in Intl && "formatToParts" in Intl.DateTimeFormat.prototype;

// https://github.com/grafana/grafana/blob/main/packages/grafana-data/src/datetime/formats.ts
const localTimeFormat = (options: Intl.DateTimeFormatOptions): string => {
	if (!hasIntlDateTimeFormatSupport()) {
		return DEFAULT_SYSTEM_DATE_FORMAT;
	}

	// https://momentjs.com/docs/#/displaying/format/
	const dateTimeFormat = new Intl.DateTimeFormat([...navigator.languages], options);
	const parts = dateTimeFormat.formatToParts(Date.now());
	const { hour12 } = dateTimeFormat.resolvedOptions();

	const mapping: Record<Intl.DateTimeFormatPartTypes, string> = {
		year: "yyyy",
		month: "MM",
		day: "dd",
		hour: hour12 ? "hh" : "HH",
		minute: "mm",
		second: "ss",
		weekday: "ddd",
		era: "N",
		dayPeriod: "a",
		timeZoneName: "Z",
		literal: "",
		unknown: "",
		fractionalSecond: "",
	};

	return parts.map(({ type, value }) => mapping[type] || value).join("");
};

export const dateTimeAsLocal = toDateTimeValueFormatter(
	localTimeFormat({
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
		hour: "2-digit",
		minute: "2-digit",
		second: "2-digit",
	}),
);

export const DateTimeAsLocalFormatNoDateIfToday = toDateTimeValueFormatter(
	localTimeFormat({
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
		hour: "2-digit",
		minute: "2-digit",
		second: "2-digit",
	}),
	localTimeFormat({
		hour: "2-digit",
		minute: "2-digit",
		second: "2-digit",
	}),
);

export const dateTimeSystemFormatter: UnitFormatter = (ms) => format(ms, DEFAULT_SYSTEM_DATE_FORMAT);

export const dateTimeFromNow: UnitFormatter = (value) => formatDistance(value, Date.now(), { addSuffix: true });
